import React, { ReactNode, useMemo } from 'react';
import {
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  AreaChart,
  Area,
  Customized,
} from 'recharts';
import { Widget } from './Widget';
import { useWidgetZoomControlsPublisher } from './WidgetZoomControlsContext';
import { DataResult, WidgetSettingsDto } from '@thingslog/repositories';
import { DataResultParser } from './parsing/DataResultParser';
import { DataResultParsed, DataResultParseStream } from './parsing/DataResultParsed';
import {
  ChartEmptyState,
  ChartDataDensityOverlay,
  ChartSeriesConfig,
  ChartTooltipContent,
  ChartWidgetFrame,
  DEFAULT_CHART_COLORS,
  chartAxisTick,
  chartGridProps,
  getChartLegendProps,
  chartMargin,
  CHART_RESIZE_DEBOUNCE_MS,
  chartTooltipWrapperStyle,
  chartYAxisWidth,
  createChartSeries,
  createSmartDateFormatter,
  decimateChartData,
  getVisibleChartData,
  LINE_AREA_MAX_RENDER_POINTS,
  LINE_AREA_SCROLL_ZOOM_POINT_THRESHOLD,
  getChartSeriesLegendKeys,
  useChartLegendVisibility,
  useChartViewportControls,
} from './utils';
import { WidgetSettingsFactory } from '../../factories/WidgetSettings.factory';

interface AreaChartContentProps {
  data: { [key: string]: string | number }[];
  series: ChartSeriesConfig[];
  parsedData: DataResultParsed;
  widgetSettings: WidgetSettingsDto;
}

const createGradientId = (
  widgetSettings: WidgetSettingsDto,
  streamIdx: number,
  vizIdx: number
): string => {
  const normalizedTitle = String(widgetSettings.title ?? 'widget')
    .replace(/[^a-zA-Z0-9]/g, '-')
    .toLowerCase();

  return `area-chart-gradient-${widgetSettings.dataSourceId}-${normalizedTitle}-${streamIdx}-${vizIdx}`;
};

const getAreaFillOpacity = (widgetSettings: WidgetSettingsDto): [number, number, number] => {
  if (widgetSettings.type !== 'area-chart') {
    return [0.35, 0.14, 0.03];
  }

  switch (widgetSettings.visualization.fillOpacity) {
    case 'subtle':
      return [0.18, 0.07, 0.01];
    case 'solid':
      return [0.55, 0.28, 0.08];
    case 'balanced':
    default:
      return [0.35, 0.14, 0.03];
  }
};

const getAreaStrokeWidth = (widgetSettings: WidgetSettingsDto): number => {
  if (widgetSettings.type !== 'area-chart') {
    return 2;
  }

  switch (widgetSettings.visualization.lineThickness) {
    case 'fine':
      return 1.5;
    case 'bold':
      return 3;
    case 'standard':
    default:
      return 2;
  }
};

const getAreaInterpolation = (
  widgetSettings: WidgetSettingsDto
): 'linear' | 'monotone' | 'stepAfter' => {
  if (widgetSettings.type !== 'area-chart') {
    return 'monotone';
  }

  switch (widgetSettings.visualization.interpolation) {
    case 'straight':
      return 'linear';
    case 'stepped':
      return 'stepAfter';
    case 'smooth':
    default:
      return 'monotone';
  }
};

const AreaChartContent = ({
  data,
  series,
  parsedData,
  widgetSettings,
}: AreaChartContentProps): JSX.Element => {
  const visualization = widgetSettings.type === 'area-chart' ? widgetSettings.visualization : null;
  const [topOpacity, middleOpacity, bottomOpacity] = getAreaFillOpacity(widgetSettings);
  const hasViewportControls =
    data.length > LINE_AREA_SCROLL_ZOOM_POINT_THRESHOLD && !!parsedData.xAxis?.dataKey;
  const {
    canZoomIn,
    canZoomOut,
    containerClassName,
    containerProps,
    containerStyle,
    isZoomed,
    resetZoom,
    viewport,
    zoomIn,
    zoomOut,
  } = useChartViewportControls({ dataLength: data.length, enabled: hasViewportControls });

  useWidgetZoomControlsPublisher(
    hasViewportControls ? { canZoomIn, canZoomOut, isZoomed, resetZoom, zoomIn, zoomOut } : null
  );

  const { isSeriesHidden, toggleSeries } = useChartLegendVisibility(
    getChartSeriesLegendKeys(series)
  );

  const visibleData = useMemo(
    () => getVisibleChartData(data, viewport, hasViewportControls),
    [data, hasViewportControls, viewport]
  );
  const renderData = useMemo(
    () => decimateChartData(visibleData, LINE_AREA_MAX_RENDER_POINTS, parsedData.xAxis?.dataKey),
    [parsedData.xAxis?.dataKey, visibleData]
  );
  const xAxisFormatter = useMemo(
    () =>
      createSmartDateFormatter(renderData, parsedData.xAxis?.dataKey, parsedData.xAxis?.formatter),
    [renderData, parsedData.xAxis?.dataKey, parsedData.xAxis?.formatter]
  );

  return (
    <div
      className={`relative h-full w-full ${containerClassName}`}
      style={containerStyle}
      {...containerProps}
    >
      <ResponsiveContainer width="100%" height="100%" debounce={CHART_RESIZE_DEBOUNCE_MS}>
        <AreaChart data={renderData} margin={chartMargin}>
          <defs>
            {series.map((item: ChartSeriesConfig) => (
              <linearGradient
                key={item.gradientId}
                id={item.gradientId ?? item.dataKey}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={item.color} stopOpacity={topOpacity} />
                <stop offset="65%" stopColor={item.color} stopOpacity={middleOpacity} />
                <stop offset="100%" stopColor={item.color} stopOpacity={bottomOpacity} />
              </linearGradient>
            ))}
          </defs>
          {visualization?.showGrid !== false && <CartesianGrid {...chartGridProps} />}
          {parsedData.xAxis &&
            parsedData.xAxis.show &&
            visualization?.showXAxisLabels !== false && (
              <XAxis
                dataKey={parsedData.xAxis.dataKey}
                tickFormatter={xAxisFormatter}
                name={parsedData.xAxis.name}
                axisLine={false}
                tickLine={false}
                tick={chartAxisTick}
                dy={8}
                minTickGap={24}
              />
            )}
          {parsedData.yAxis &&
            parsedData.yAxis.show &&
            visualization?.showYAxisLabels !== false && (
              <YAxis
                tickFormatter={parsedData.yAxis?.formatter}
                name={parsedData.yAxis?.name}
                axisLine={false}
                tickLine={false}
                tick={chartAxisTick}
                width={chartYAxisWidth}
              />
            )}
          {series.map((item: ChartSeriesConfig) => (
            <Area
              key={item.dataKey}
              type={getAreaInterpolation(widgetSettings)}
              dataKey={item.dataKey}
              name={item.name}
              unit={item.unit}
              stackId={visualization?.stacked === true ? '1' : undefined}
              stroke={item.color}
              strokeWidth={getAreaStrokeWidth(widgetSettings)}
              fill={`url(#${item.gradientId ?? item.dataKey})`}
              activeDot={{
                r: 5,
                stroke: '#ffffff',
                strokeWidth: 2,
                fill: item.color,
              }}
              dot={false}
              connectNulls={true}
              isAnimationActive={false}
              hide={isSeriesHidden(item.dataKey)}
            />
          ))}
          <Tooltip
            wrapperStyle={chartTooltipWrapperStyle}
            content={
              <ChartTooltipContent
                fallbackColors={DEFAULT_CHART_COLORS}
                labelFormatter={(
                  value: string | number,
                  dataPoint?: { [key: string]: string | number }
                ): string =>
                  parsedData.tooltip?.labelFormatter?.(value, dataPoint) ?? String(value)
                }
                valueFormatter={
                  parsedData.tooltip?.formatter
                    ? (value: number | string, name: string): [string, string] =>
                        parsedData.tooltip!.formatter!(value, name, parsedData.dataStreams)
                    : undefined
                }
              />
            }
          />
          {parsedData.legend && parsedData.legend.show && visualization?.showLegend !== false && (
            <Legend
              iconType="circle"
              payload={series.map((item: ChartSeriesConfig) => ({
                color: item.color,
                id: item.dataKey,
                inactive: isSeriesHidden(item.dataKey),
                type: 'circle' as const,
                value: item.name,
              }))}
              {...getChartLegendProps({
                aside: visualization?.renderLegendAside === true,
                onToggleSeries: series.length > 1 ? toggleSeries : undefined,
              })}
            />
          )}
          {hasViewportControls && (
            <Customized
              component={
                <ChartDataDensityOverlay
                  renderedPoints={renderData.length}
                  totalPoints={visibleData.length}
                />
              }
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export class AreaChartWidget implements Widget {
  public hasScrollZoomControls(data: DataResult, widgetSettings: WidgetSettingsDto): boolean {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);
    const dataParser = new DataResultParser(widgetSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);

    return (
      dataParser.merge(parsedData).length > LINE_AREA_SCROLL_ZOOM_POINT_THRESHOLD &&
      !!parsedData.xAxis?.dataKey
    );
  }

  public render(data: DataResult, widgetSettings: WidgetSettingsDto): ReactNode {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);
    const dataParser = new DataResultParser(widgetSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);
    const noData = parsedData.dataStreams.every(
      (stream: DataResultParseStream) => stream.data.length === 0
    );

    if (noData) {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_widget_no_areas"
          description="This widget has no areas to display."
        />
      );
    }

    const mergedData = dataParser.merge(parsedData);
    const series = createChartSeries(parsedData.dataStreams, {
      createGradientId: (streamIdx: number, vizIdx: number): string =>
        createGradientId(widgetSettings, streamIdx, vizIdx),
      getColor: ({ fill, stroke }: { fill?: string; stroke?: string }): string | undefined =>
        fill ?? stroke,
    });

    return (
      <ChartWidgetFrame>
        <div className="min-h-0 flex-1">
          <AreaChartContent
            data={mergedData}
            series={series}
            parsedData={parsedData}
            widgetSettings={normalizedSettings}
          />
        </div>
      </ChartWidgetFrame>
    );
  }
}
